/*
 *
 *  * Copyright (c) [hoarfroster-frontend] 2026. All Rights Reserved.
 *  *
 *  * Last Modified on Jun 21, 2026 by hoarfroster
 *
 */

import { Link } from 'react-router';
import React, { JSX, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { UserRole } from '@/models/user/user';
import I18N from '@utils/i18n.base';
import { canEditCooking, isUserInRole } from '@utils/user-info';
import LanguageSwitcher from '@components/language-switcher';
import ThemeSwitcher from '@components/theme-switcher';
import { setCookie } from '@utils/cookie.ts';
import UserState from '@components/header/user-state.tsx';
import GlobalSearch from '@components/search/GlobalSearch';
import { buildSearchScopes } from '@components/header/search-scopes';

interface NavChild {
    id: string;
    label: string;
    to: string;
    icon?: string;
    description?: string;
}

interface NavItem {
    id: string;
    label: string;
    to?: string;
    icon?: string;
    children?: NavChild[];
}

/**
 * LocalDialog: a simple modal dialog using theme colors
 */
function LocalDialog({
    open,
    onClose,
    title,
    size = 'md',
    children,
}: {
    open: boolean;
    onClose: () => void;
    title: string;
    size?: 'sm' | 'md' | 'lg' | 'xl';
    children: React.ReactNode;
}) {
    const dialogRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        if (!open) return;
        const previousOverflow = document.body.style.overflow;
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', handleKeyDown);
        document.body.style.overflow = 'hidden';
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            document.body.style.overflow = previousOverflow;
        };
    }, [open, onClose]);

    if (!open) return null;

    let widthClass = 'w-full max-w-md';
    if (size === 'sm') widthClass = 'w-full max-w-sm';
    if (size === 'lg') widthClass = 'w-full max-w-lg';
    if (size === 'xl') widthClass = 'w-full max-w-xl';

    return createPortal(
        <div
            className="fixed inset-0 z-50 flex h-dvh w-screen items-center justify-center p-4"
            style={{ background: 'rgba(0,0,0,0.4)' }}
            onClick={onClose}
        >
            <div
                ref={dialogRef}
                className={`bg-theme-popup-bg border-theme-border text-theme-text max-h-[calc(100dvh-2rem)] overflow-hidden rounded-lg border shadow-xl ${widthClass}`}
                style={{ boxShadow: '0 16px 48px rgba(15,23,42,0.18)' }}
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
                aria-labelledby="local-dialog-title"
            >
                <div className="border-theme-border flex items-center justify-between border-b px-5 py-3">
                    <h2 id="local-dialog-title" className="text-base font-semibold">
                        {title}
                    </h2>
                    <button
                        aria-label="Close dialog"
                        className="hover:bg-theme-hover rounded-md p-1.5"
                        onClick={onClose}
                        type="button"
                    >
                        <span className="material-symbols-rounded text-xl">close</span>
                    </button>
                </div>
                <div className="max-h-[calc(100dvh-6rem)] overflow-auto p-5">{children}</div>
            </div>
        </div>,
        document.body
    );
}

/**
 * Application header/navigation bar component.
 *
 * @returns {JSX.Element} The rendered header component.
 */
export default function Header({ name }: { name?: string }): JSX.Element {
    const isSU = isUserInRole(UserRole.SuperAdmin);
    const hasCookingEditAccess = canEditCooking();
    const searchScopes = useMemo(() => buildSearchScopes(isSU), [isSU]);

    const navItems = useMemo<NavItem[]>(() => {
        const noteChildren: NavChild[] = [
            { id: 'notes-home', label: I18N.note.title, to: '/note', icon: 'description' },
        ];

        if (isSU) {
            noteChildren.push(
                {
                    id: 'notes-pinned',
                    label: 'Pinned Notes',
                    to: '/note/pinned',
                    icon: 'keep',
                    description: 'Batch manage pinned notes',
                },
                {
                    id: 'notes-collections',
                    label: I18N.note.collections,
                    to: '/note/collections',
                    icon: 'folder_special',
                    description: 'Manage and organize note collections',
                },
                { id: 'notes-moments', label: I18N.app.moments, to: '/moment', icon: 'party_mode' },
                { id: 'notes-remarks', label: I18N.app.remarks, to: '/remark', icon: 'format_quote' }
            );
        }

        const baseItems: NavItem[] = [
            {
                id: 'notes',
                label: I18N.note.title,
                to: '/note',
                icon: 'description',
                children: noteChildren,
            },
            {
                id: 'cooking',
                label: I18N.cooking.cooking,
                icon: 'cooking',
                children: [
                    { id: 'cook-recipes', label: I18N.cooking.recipeList, to: '/cooking/recipes', icon: 'menu_book' },
                    ...(hasCookingEditAccess
                        ? [
                              {
                                  id: 'cook-new',
                                  label: I18N.cooking.createRecipe,
                                  to: '/cooking/recipes/new',
                                  icon: 'add',
                              },
                          ]
                        : []),
                    {
                        id: 'cook-ingredients',
                        label: I18N.cooking.ingredients,
                        to: '/cooking/ingredients',
                        icon: 'restaurant',
                    },
                    { id: 'cook-tools', label: I18N.cooking.tools, to: '/cooking/tools', icon: 'build' },
                ],
            },
        ];

        if (isSU) {
            baseItems.push(
                {
                    id: 'files',
                    label: I18N.file.files,
                    icon: 'folder',
                    children: [
                        { id: 'file-browse', label: I18N.file.fileList, to: '/file/browse', icon: 'list' },
                        {
                            id: 'file-live',
                            label: 'Live Photo Upload',
                            to: '/file/live-photo-upload',
                            icon: 'photo_camera',
                        },
                        { id: 'file-tag', label: I18N.file.fileTag, to: '/file/tag', icon: 'tag' },
                    ],
                },
                {
                    id: 'activity',
                    label: I18N.app.timeActivity,
                    icon: 'calendar_month',
                    children: [
                        { id: 'activity-board', label: I18N.app.timeActivity, to: '/time/project', icon: 'timer' },
                        { id: 'activity-calendar', label: 'Calendar', to: '/time/calendar', icon: 'calendar_month' },
                    ],
                },
                {
                    id: 'user',
                    label: I18N.admin.user ?? 'User',
                    icon: 'person',
                    children: [
                        { id: 'user-contact', label: I18N.app.contacts, to: '/contact', icon: 'contact_page' },
                        {
                            id: 'contact-invitations',
                            label: I18N.contact.contactInvitations,
                            to: '/admin/contact-invitations',
                            icon: 'forward_to_inbox',
                        },
                        {
                            id: 'user-credentials',
                            label: I18N.app.credentials,
                            to: '/user/credentials',
                            icon: 'passkey',
                        },
                        {
                            id: 'admin-widgets',
                            label: 'Widgets',
                            to: '/admin/widgets',
                            icon: 'widgets',
                            description: 'Configure dashboard widget types',
                        },
                    ],
                }
            );
        }

        return baseItems;
    }, [hasCookingEditAccess, isSU]);

    const navRowRef = useRef<HTMLDivElement | null>(null);
    const navItemRefs = useRef<Record<string, HTMLDivElement | null>>({});
    const navItemWidths = useRef<Record<string, number>>({});
    const dropdownCloseTimer = useRef<number | null>(null);

    const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
    const [visibleNavItems, setVisibleNavItems] = useState<Set<string>>(() => new Set(navItems.map((item) => item.id)));

    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [isLanguageOpen, setIsLanguageOpen] = useState(false);
    const [isThemeOpen, setIsThemeOpen] = useState(false);
    const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});
    const [isSignOutOpen, setIsSignOutOpen] = useState(false);

    const recalcVisibleNav = useCallback(() => {
        const containerWidth = navRowRef.current?.getBoundingClientRect().width ?? 0;
        const availableWidth = Math.max(containerWidth - 24, 0);
        let usedWidth = 0;
        const nextVisible = new Set<string>();

        navItems.forEach((item) => {
            const el = navItemRefs.current[item.id];
            const measuredWidth = el?.getBoundingClientRect().width ?? 0;
            if (measuredWidth > 0) {
                navItemWidths.current[item.id] = measuredWidth;
            }
            const width = navItemWidths.current[item.id] ?? measuredWidth;

            if (!width || width === 0) {
                nextVisible.add(item.id);
                return;
            }

            if (usedWidth + width <= availableWidth) {
                nextVisible.add(item.id);
                usedWidth += width;
            }
        });

        if (nextVisible.size === 0) {
            setVisibleNavItems(new Set(navItems.map((item) => item.id)));
            return;
        }

        setVisibleNavItems(nextVisible);
    }, [navItems]);

    useLayoutEffect(() => {
        const resizeObserver = new ResizeObserver(recalcVisibleNav);
        if (navRowRef.current) resizeObserver.observe(navRowRef.current);
        window.addEventListener('resize', recalcVisibleNav);
        recalcVisibleNav();
        return () => {
            resizeObserver.disconnect();
            window.removeEventListener('resize', recalcVisibleNav);
        };
    }, [recalcVisibleNav]);

    const toggleMobileMenu = () => {
        setIsMobileMenuOpen((prev) => !prev);
    };

    const closeMobileMenu = () => {
        setIsMobileMenuOpen(false);
    };

    const confirmSignOut = useCallback(() => {
        setCookie('token', '');
        localStorage.setItem('user-info', '');
        window.location.href = '/';
    }, []);

    const openDropdown = useCallback((id: string) => {
        if (dropdownCloseTimer.current !== null) {
            window.clearTimeout(dropdownCloseTimer.current);
            dropdownCloseTimer.current = null;
        }
        setActiveDropdown(id);
    }, []);

    const closeDropdownDelayed = useCallback(() => {
        if (dropdownCloseTimer.current !== null) {
            window.clearTimeout(dropdownCloseTimer.current);
        }
        dropdownCloseTimer.current = window.setTimeout(() => setActiveDropdown(null), 120);
    }, []);

    const renderDesktopNavItem = (item: NavItem) => {
        const hasChildren = (item.children?.length ?? 0) > 0;
        const isVisible = visibleNavItems.has(item.id);
        const target = item.to ?? item.children?.[0]?.to ?? '#';

        return (
            <div
                key={item.id}
                ref={(node) => {
                    navItemRefs.current[item.id] = node;
                }}
                className={`${isVisible ? 'relative' : 'pointer-events-none absolute opacity-0'} flex h-full items-center`}
                onMouseEnter={() => hasChildren && openDropdown(item.id)}
            >
                <Link
                    to={target}
                    className={`text-theme-text hover:text-theme-active flex h-full items-center gap-1 rounded-lg px-3 py-2 font-semibold transition-colors duration-200 ${
                        activeDropdown === item.id ? 'text-theme-active' : ''
                    }`}
                >
                    <span>{item.label}</span>
                    {hasChildren && (
                        <span className="material-symbols-rounded text-theme-icon text-sm opacity-60">expand_more</span>
                    )}
                </Link>
            </div>
        );
    };

    return (
        <header className="sticky top-0 z-40 break-keep">
            {/* Backdrop for the entire screen when the menu is open */}
            <div
                className={`fixed inset-0 hidden h-screen w-full bg-black/20 backdrop-blur-xl transition-[opacity,visibility] duration-700 ease-[cubic-bezier(0.32,0.72,0,1)] lg:block ${
                    activeDropdown ? 'visible opacity-100' : 'pointer-events-none invisible opacity-0'
                }`}
                style={{ zIndex: 0 }}
            />

            {/* Main Header + Dropdown wrapper with unified background */}
            <div
                className={`relative z-10 w-full transition-colors duration-300 ${
                    activeDropdown
                        ? 'bg-theme-content-bg shadow-sm'
                        : 'bg-theme-bg/80 border-theme-border border-b backdrop-blur-md'
                }`}
                onMouseLeave={closeDropdownDelayed}
                onMouseEnter={() => {
                    if (dropdownCloseTimer.current !== null) {
                        window.clearTimeout(dropdownCloseTimer.current);
                        dropdownCloseTimer.current = null;
                    }
                }}
            >
                <nav
                    className="relative z-10 mx-auto flex h-15 items-center justify-between px-4 sm:h-14 sm:px-8"
                    aria-label="Global"
                >
                    <div className="flex h-full items-center lg:flex-1">
                        {/* Mobile Menu Button */}
                        <button
                            onClick={toggleMobileMenu}
                            className="text-theme-text-light hover:bg-theme-hover mr-4 flex items-center justify-center rounded-lg p-2 lg:hidden"
                        >
                            <span className="material-symbols-rounded text-xl">menu</span>
                        </button>

                        <div className="flex items-center">
                            <Link to="/" className="text-theme-text -m-1.5 flex items-center space-x-4 p-1.5 font-bold">
                                <img src="/favicon.avif" alt="Logo" className="h-8 w-8 rounded-full" />
                                <span>{I18N.app.appName}</span>
                            </Link>
                            {name && (
                                <>
                                    <div className="bg-theme-line ml-4 h-8 w-0.5" />
                                    <span className="ml-4 text-sm font-bold">{name}</span>
                                </>
                            )}
                        </div>

                        {/* Desktop Navigation */}
                        <div ref={navRowRef} className="hidden pl-4 font-semibold lg:mx-8 lg:flex lg:gap-x-2">
                            {navItems.map((item) => renderDesktopNavItem(item))}
                        </div>
                    </div>

                    <div className="flex items-center space-x-4">
                        {/* Quick switching button: localization and theme */}
                        <div className="hidden items-center space-x-2 sm:flex">
                            <button
                                type="button"
                                aria-label={I18N.app.languageSettings}
                                className="hover:bg-theme-hover text-theme-text-light border-theme-border bg-theme-content-bg flex h-9 w-9 items-center justify-center rounded-lg border"
                                onClick={() => setIsLanguageOpen(true)}
                            >
                                <span className="material-symbols-rounded">translate</span>
                            </button>
                            <button
                                type="button"
                                aria-label={I18N.app.themeColor}
                                className="hover:bg-theme-hover text-theme-text-light border-theme-border bg-theme-content-bg flex h-9 w-9 items-center justify-center rounded-lg border"
                                onClick={() => setIsThemeOpen(true)}
                            >
                                <span className="material-symbols-rounded">dark_mode</span>
                            </button>
                        </div>

                        <GlobalSearch
                            scopes={searchScopes}
                            defaultScopeKey="notes"
                            className="hidden w-64 md:block lg:w-72"
                        />

                        <div className="bg-theme-input-border h-6 w-px"></div>

                        {/* User Menu */}
                        <div className="relative flex items-center">
                            <UserState mobile={false} onSignOut={() => setIsSignOutOpen(true)} />
                        </div>
                    </div>

                    {/* Mobile Menu Overlay */}
                    {isMobileMenuOpen && (
                        <div className="fixed inset-0 z-50 lg:hidden">
                            {/* Backdrop */}
                            <div className="absolute inset-0 bg-black/50" onClick={closeMobileMenu}></div>

                            {/* Mobile Sidebar */}
                            <div className="bg-theme-card-bg absolute top-0 left-0 h-screen w-80 max-w-[80vw] shadow-xl">
                                {/* Mobile Header */}
                                <div className="border-theme-border flex items-center justify-between border-b p-4">
                                    <h2 className="text-theme-text text-lg font-semibold">Menu</h2>
                                    <button
                                        onClick={closeMobileMenu}
                                        className="text-theme-text-light hover:bg-theme-hover flex items-center justify-center rounded-lg p-2"
                                    >
                                        <span className="material-symbols-rounded text-xl">close</span>
                                    </button>
                                </div>

                                {/* Mobile Search */}
                                <div className="border-theme-border border-b p-4">
                                    <GlobalSearch scopes={searchScopes} defaultScopeKey="notes" className="w-full" />
                                </div>

                                {/* Mobile Settings */}
                                <div className="border-theme-border border-b p-4">
                                    <div className="flex items-center justify-center space-x-4">
                                        <button
                                            type="button"
                                            aria-label={I18N.app.languageSettings}
                                            className="hover:bg-theme-hover text-theme-text-light border-theme-border bg-theme-content-bg flex h-10 w-10 items-center justify-center rounded-lg border"
                                            onClick={() => {
                                                setIsLanguageOpen(true);
                                                closeMobileMenu();
                                            }}
                                        >
                                            <span className="material-symbols-rounded">translate</span>
                                        </button>
                                        <button
                                            type="button"
                                            aria-label={I18N.app.themeColor}
                                            className="hover:bg-theme-hover text-theme-text-light border-theme-border bg-theme-content-bg flex h-10 w-10 items-center justify-center rounded-lg border"
                                            onClick={() => {
                                                setIsThemeOpen(true);
                                                closeMobileMenu();
                                            }}
                                        >
                                            <span className="material-symbols-rounded">dark_mode</span>
                                        </button>
                                    </div>
                                </div>

                                {/* Mobile Navigation */}
                                <nav className="flex flex-col space-y-2 p-4">
                                    {navItems.map((item) => {
                                        const hasChildren = (item.children?.length ?? 0) > 0;
                                        const expanded = expandedSections[item.id] ?? false;
                                        const target = item.to ?? item.children?.[0]?.to ?? '#';

                                        if (!hasChildren) {
                                            return (
                                                <Link
                                                    key={item.id}
                                                    to={target}
                                                    onClick={closeMobileMenu}
                                                    className="text-theme-text-light hover:bg-theme-hover flex items-center rounded-lg px-4 py-3 text-base font-medium"
                                                >
                                                    {item.icon && (
                                                        <span className="material-symbols-rounded mr-3 text-xl">
                                                            {item.icon}
                                                        </span>
                                                    )}
                                                    {item.label}
                                                </Link>
                                            );
                                        }

                                        return (
                                            <div key={item.id} className="flex flex-col">
                                                <button
                                                    onClick={() =>
                                                        setExpandedSections((prev) => ({
                                                            ...prev,
                                                            [item.id]: !expanded,
                                                        }))
                                                    }
                                                    className="text-theme-text-light hover:bg-theme-hover flex items-center rounded-lg px-4 py-3 text-left text-base font-medium"
                                                >
                                                    {item.icon && (
                                                        <span className="material-symbols-rounded mr-3 text-xl">
                                                            {item.icon}
                                                        </span>
                                                    )}
                                                    {item.label}
                                                    <span className="material-symbols-rounded ml-auto text-xl">
                                                        {expanded ? 'expand_less' : 'expand_more'}
                                                    </span>
                                                </button>
                                                {expanded && (
                                                    <div className="mt-1 ml-8 flex flex-col space-y-1">
                                                        {item.children?.map((child) => (
                                                            <Link
                                                                key={child.id}
                                                                to={child.to}
                                                                onClick={closeMobileMenu}
                                                                className="text-theme-text-light hover:bg-theme-hover flex items-center rounded-lg px-4 py-2 text-sm"
                                                            >
                                                                {child.icon && (
                                                                    <span className="material-symbols-rounded mr-3 text-lg">
                                                                        {child.icon}
                                                                    </span>
                                                                )}
                                                                {child.label}
                                                            </Link>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </nav>
                            </div>
                        </div>
                    )}

                    {/* Language Settings Dialog (local) */}
                    <LocalDialog
                        open={isLanguageOpen}
                        onClose={() => setIsLanguageOpen(false)}
                        title={I18N.app.languageSettings}
                        size="lg"
                    >
                        <LanguageSwitcher />
                    </LocalDialog>

                    {/* Theme Settings Dialog (local) */}
                    <LocalDialog
                        open={isThemeOpen}
                        onClose={() => setIsThemeOpen(false)}
                        title={I18N.app.themeColor}
                        size="xl"
                    >
                        <ThemeSwitcher />
                    </LocalDialog>

                    {/* Sign-out confirmation */}
                    <LocalDialog
                        open={isSignOutOpen}
                        onClose={() => setIsSignOutOpen(false)}
                        title={I18N.auth.signOut}
                        size="sm"
                    >
                        <p className="text-theme-text-light mb-4 text-sm">{I18N.auth.signOutConfirmation}</p>
                        <div className="flex justify-end gap-3">
                            <button
                                type="button"
                                className="text-theme-text hover:bg-theme-hover border-theme-border bg-theme-content-bg rounded-lg border px-3 py-2 text-sm font-semibold"
                                onClick={() => setIsSignOutOpen(false)}
                            >
                                {I18N.app.cancel}
                            </button>
                            <button
                                type="button"
                                className="bg-theme-error text-theme-popup-bg rounded-lg px-3 py-2 text-sm font-semibold hover:opacity-90"
                                onClick={confirmSignOut}
                            >
                                {I18N.auth.signOut}
                            </button>
                        </div>
                    </LocalDialog>
                </nav>

                {/* Desktop Expanded Content Area */}
                <div
                    className={`bg-theme-content-bg absolute top-full left-0 z-0 hidden w-full origin-top overflow-hidden transition-[max-height,padding,border-color] duration-700 ease-[cubic-bezier(0.32,0.72,0,1)] lg:block ${
                        activeDropdown
                            ? 'border-theme-border max-h-100 border-b pb-8'
                            : 'pointer-events-none max-h-0 border-transparent pb-0'
                    }`}
                >
                    <div
                        className={`mx-auto flex w-full gap-12 px-4 pt-8 pl-60 transition-all delay-100 duration-500 ease-[cubic-bezier(0.32,0.72,0,1)] sm:px-8 ${
                            activeDropdown ? 'translate-y-0 opacity-100' : '-translate-y-4 opacity-0'
                        }`}
                    >
                        {navItems
                            .find((i) => i.id === activeDropdown)
                            ?.children?.map((child) => (
                                <div key={child.id} className="flex flex-col gap-4">
                                    <Link
                                        to={child.to}
                                        className="group text-theme-text hover:text-theme-active flex items-start gap-4 transition-colors duration-200"
                                        onClick={() => {
                                            setActiveDropdown(null);
                                            if (dropdownCloseTimer.current !== null)
                                                window.clearTimeout(dropdownCloseTimer.current);
                                        }}
                                    >
                                        {child.icon && (
                                            <span className="material-symbols-rounded text-theme-icon group-hover:text-theme-active mt-0.5 text-2xl">
                                                {child.icon}
                                            </span>
                                        )}
                                        <div className="flex max-w-50 flex-col">
                                            <span className="text-base leading-6 font-bold">{child.label}</span>
                                            {child.description && (
                                                <span className="text-theme-text-light mt-1 text-xs leading-relaxed font-normal">
                                                    {child.description}
                                                </span>
                                            )}
                                        </div>
                                    </Link>
                                </div>
                            ))}
                    </div>
                </div>
            </div>
        </header>
    );
}
